import { useState, type ReactNode } from 'react';
import { Page, PageHeader } from '../components/Page';
import { PCard } from '../components/PCard';
import { StatusPill } from '../components/StatusPill';
import { InvoiceTable } from '../components/InvoiceTable';
import { Async } from '../components/Async';
import { IcTruck } from '../components/icons';
import { useFetch } from '../lib/useFetch';
import { useAuth } from '../lib/auth';
import {
  getInvoices,
  getClientOptions,
  getShippingRates,
  setShippingRate,
  deleteShippingRate,
  ApiError,
  type ShippingService,
  type ShippingRateRow,
} from '../lib/api';
import { fmtCents } from '../lib/format';

export const ShippingFeesPage = ({ tabs }: { tabs?: ReactNode } = {}) => {
  // Shipping is billed on the combined pharmacy invoice, so the list shows those.
  const invoices = useFetch(() => getInvoices({ flow: 'pharmacy' }));

  return (
    <Page>
      <PageHeader
        tabs={tabs}
        title="Shipping Fees"
        sub="FedEx shipments from pharmacyV2 · billed on the pharmacy invoice via Hyperswitch · coverage model"
      />

      <PCard style={{ padding: 16, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <div className="p-stat__icon p-stat__icon--in-transit">
          <IcTruck size={18} />
        </div>
        <div style={{ fontSize: 13, color: 'var(--p-muted-fg)' }}>
          Set shipping rates below — per-clinic overrides win over the global default. Each FedEx shipment
          (a pharmacyV2 fill with a service class) from a COMPLETED order is priced once and billed on the
          client's <strong style={{ color: 'var(--p-foreground)' }}>combined pharmacy invoice</strong> —
          there is no separate shipping run. Generate a client's draft from the client detail or Pharmacy Fees page.
        </div>
      </PCard>

      <ShippingRates />

      <div>
        <div className="section-title">Pharmacy + shipping invoices</div>
        <Async state={invoices} isEmpty={(d) => d.invoices.length === 0} emptyLabel="No invoices yet.">
          {({ invoices: rows }) => <InvoiceTable invoices={rows} showType={false} />}
        </Async>
      </div>
    </Page>
  );
};

const GLOBAL = 'global';

const SERVICES: { key: ShippingService; label: string; hint: string }[] = [
  { key: 'overnight', label: 'Overnight', hint: 'per shipment' },
  { key: '2day', label: '2-Day', hint: 'per shipment' },
  { key: 'sat_surcharge', label: 'Saturday surcharge', hint: 'added to overnight' },
];

function ShippingRates() {
  const { can } = useAuth();
  const manage = can('pricing:manage');
  const [scope, setScope] = useState<string>(GLOBAL); // 'global' or a clientId
  const [error, setError] = useState<string | null>(null);

  const clientsState = useFetch(getClientOptions);
  const globalState = useFetch(() => getShippingRates({ scope: 'global' }));
  const overrideState = useFetch(
    () => (scope === GLOBAL ? Promise.resolve({ rates: [] }) : getShippingRates({ clientId: scope })),
    [scope],
  );

  const clients = clientsState.data?.clients ?? [];
  const isClient = scope !== GLOBAL;
  const scopeLabel = isClient ? clients.find((c) => c.id === scope)?.name ?? 'Customer' : 'Global default';

  const globalBy = byService(globalState.data?.rates);
  const overrideBy = byService(overrideState.data?.rates);

  async function run(fn: () => Promise<unknown>) {
    setError(null);
    try {
      await fn();
      globalState.reload();
      overrideState.reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Action failed');
    }
  }

  function editPrice(svc: ShippingService, label: string, current?: number) {
    const v = window.prompt(`${label} price for ${scopeLabel} (dollars):`, ((current ?? 0) / 100).toString());
    const dollars = v != null ? parseFloat(v) : NaN;
    if (!Number.isNaN(dollars) && dollars >= 0) {
      run(() => setShippingRate({ clientId: isClient ? scope : null, service: svc, priceCents: Math.round(dollars * 100) }));
    }
  }

  const satTotal = (over?: number) =>
    over != null && globalBy.sat_surcharge ? over + (overrideBy.sat_surcharge?.priceCents ?? globalBy.sat_surcharge.priceCents) : undefined;

  // Effective overnight (override if set, else global) used to compute the Saturday total.
  const effOvernight = (overrideBy.overnight ?? globalBy.overnight)?.priceCents;
  const satComputed = satTotal(effOvernight);

  return (
    <div>
      <div className="section-title" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        Shipping rates
        <select className="select" value={scope} onChange={(e) => setScope(e.target.value)} style={{ marginLeft: 'auto' }}>
          <option value={GLOBAL}>Global default</option>
          {clients.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name} ({c.type})
            </option>
          ))}
        </select>
      </div>

      {error && (
        <div style={{ padding: '8px 12px', borderRadius: 6, background: 'rgba(239,68,68,.08)', color: 'var(--status-error-ink,#b91c1c)', fontSize: 13, marginBottom: 8 }}>
          {error}
        </div>
      )}

      <PCard>
        <table className="p-table">
          <thead>
            <tr>
              <th>Service</th>
              <th>Scope</th>
              <th style={{ textAlign: 'right' }}>Price</th>
              {manage && <th style={{ textAlign: 'right' }}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {SERVICES.map(({ key, label, hint }) => {
              const override = overrideBy[key];
              const global = globalBy[key];
              const effective = isClient ? override ?? global : global;
              const inherited = isClient && !override;
              return (
                <tr key={key}>
                  <td>
                    <div style={{ fontWeight: 500 }}>{label}</div>
                    <div className="muted" style={{ fontSize: 12 }}>{hint}</div>
                  </td>
                  <td>
                    {!effective ? (
                      <StatusPill tone="neutral">Unset</StatusPill>
                    ) : inherited ? (
                      <StatusPill tone="neutral">Global</StatusPill>
                    ) : isClient ? (
                      <StatusPill tone="in-progress">Override</StatusPill>
                    ) : (
                      <StatusPill tone="neutral">Global</StatusPill>
                    )}
                  </td>
                  <td className="tabular" style={{ textAlign: 'right' }}>
                    {effective ? fmtCents(effective.priceCents) : '—'}
                  </td>
                  {manage && (
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'inline-flex', gap: 8 }}>
                        <button className="btn" onClick={() => editPrice(key, label, effective?.priceCents)}>
                          {inherited || (isClient && !override) ? 'Override' : 'Edit'}
                        </button>
                        {isClient && override && (
                          <button
                            className="btn"
                            onClick={() => {
                              if (window.confirm(`Remove ${scopeLabel}'s ${label} override (revert to global)?`))
                                run(() => deleteShippingRate(override.id));
                            }}
                          >
                            Reset
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
            <tr style={{ borderTop: '2px solid var(--p-border)' }}>
              <td>
                <div style={{ fontWeight: 500 }}>Saturday overnight</div>
                <div className="muted" style={{ fontSize: 12 }}>overnight + surcharge</div>
              </td>
              <td className="muted" style={{ fontSize: 12 }}>computed</td>
              <td className="tabular" style={{ textAlign: 'right', fontWeight: 600 }}>
                {satComputed != null ? fmtCents(satComputed) : '—'}
              </td>
              {manage && <td />}
            </tr>
          </tbody>
        </table>
      </PCard>
    </div>
  );
}

function byService(rows?: ShippingRateRow[]): Partial<Record<ShippingService, ShippingRateRow>> {
  const out: Partial<Record<ShippingService, ShippingRateRow>> = {};
  for (const r of rows ?? []) out[r.service] = r;
  return out;
}
